import {
  useCallback,
  useEffect,
  useState,
  type ReactElement,
  type ReactNode,
  type PointerEvent,
} from 'react';
import { Alert } from 'antd';
import {
  Bar,
  BarChart,
  Rectangle,
  Tooltip,
  XAxis,
  YAxis,
  type BarRectangleItem,
  type BarShapeProps,
  type TooltipContentProps,
} from 'recharts';
import { calculateNiceTicks } from '../../../utils/chart';
import { formatCount, formatAny } from '../../../utils/format';
import { TooltipContent } from './Tooltip';
import { HistogramFilter } from './HistogramFilter';

const DEFAULT_COLOR = '#023894';

const CHART_X_AXIS_SELECTOR = 'g.recharts-xAxis';
const CHART_Y_AXIS_SELECTOR = 'g.recharts-yAxis';

type HistogramItem = {
  range: [number, number];
  value: number;
  color?: string;
};

type DomPosition = {
  parentTop: number;
  parentLeft: number;
  top: number;
  left: number;
  width: number;
  height: number;
};

type HistogramProps = {
  data: HistogramItem[];
  rangeType: 'time' | 'duration' | 'distance' | 'count';
  maxDomain?: number | null;
  noDataMessage: string;
  unit?: string;
  hiddenFilter?: boolean;
  filterRange?: [number, number] | null;
  onFilterRangeChange?: (filterRange: [number, number] | null) => void;
  onBarClick?: (range: [number, number]) => void;
};

const HistogramChart = ({
  data,
  rangeType,
  maxDomain,
  noDataMessage,
  unit,
  onBarClick,
  onFilterRangeChange,
  filterRange,
  hiddenFilter = false,
}: HistogramProps): ReactNode => {
  const [barChartElement, setBarChartElement] = useState<SVGSVGElement | null>(
    null
  );
  const [plotAreaPosition, setPlotAreaPosition] = useState<DomPosition | null>(
    null
  );

  // チャートの要素を取得するためのcallback refを作成する
  const barChartRef = useCallback((element: SVGSVGElement | null) => {
    setBarChartElement(element);
  }, []);

  const updatePlotAreaPosition = useCallback((): void => {
    if (barChartElement) {
      const containerRect =
        barChartElement.parentElement?.getBoundingClientRect();
      const chartXAxisRect =
        barChartElement
          .querySelector(CHART_X_AXIS_SELECTOR)
          ?.getBoundingClientRect() ?? null;
      const chartYAxisRect =
        barChartElement
          .querySelector(CHART_Y_AXIS_SELECTOR)
          ?.getBoundingClientRect() ?? null;

      if (containerRect && chartXAxisRect && chartYAxisRect) {
        setPlotAreaPosition({
          parentTop: containerRect.top,
          parentLeft: containerRect.left,
          top: chartYAxisRect.top,
          left: chartXAxisRect.left,
          width: chartXAxisRect.width,
          height: chartYAxisRect.height,
        });
      }
    }
  }, [barChartElement]);

  useEffect((): void | (() => void) => {
    const xAxisElement = barChartElement?.querySelector(CHART_X_AXIS_SELECTOR);
    if (xAxisElement) {
      // 再レンダリングや画面幅の変更などでサイズが変わった場合にオーバーレイの位置とサイズを更新する
      const observer = new ResizeObserver(updatePlotAreaPosition);
      observer.observe(xAxisElement);
      return (): void => {
        observer.disconnect();
      };
    }
  }, [barChartElement, updatePlotAreaPosition]);

  const step = (data[0]?.range[1] ?? 1) - (data[0]?.range[0] ?? 0);
  const minRange = data[0]?.range[0] ?? 0;
  const maxRange = data[data.length - 1]?.range[1] ?? 0;
  const maxValue = Math.max(
    ...data.map((item: HistogramItem): number => {
      return item.value;
    })
  );

  if (data.length === 0) {
    return (
      <Alert
        type="warning"
        showIcon
        message={noDataMessage}
        style={{ borderRadius: '8px', marginBottom: '16px' }}
      />
    );
  }
  return (
    <div style={{ position: 'relative' }}>
      <BarChart
        ref={barChartRef}
        data={data}
        responsive
        width="100%"
        height={160}
        barCategoryGap={0.5}
        margin={{
          top: 8,
          right: 8,
        }}
      >
        <XAxis
          dataKey="range[0]"
          xAxisId="range[0]"
          type="number"
          domain={[minRange, maxRange]}
          ticks={calculateNiceTicks(rangeType, minRange, maxRange)}
          tickFormatter={(value: number): string => {
            return formatAny(rangeType, value);
          }}
          fontSize={12}
        />
        <YAxis
          dataKey="value"
          type="number"
          domain={[0, maxDomain ?? maxValue]}
          ticks={calculateNiceTicks('count', 0, maxDomain ?? maxValue)}
          tickFormatter={(value: number): string => {
            return formatCount(value);
          }}
          fontSize={12}
          width="auto"
        />
        <Tooltip
          content={({ active, payload }: TooltipContentProps): ReactNode => {
            if (active && payload && payload.length > 0) {
              const data: HistogramItem = payload[0].payload;
              return (
                <TooltipContent
                  label={`${formatAny(rangeType, data.range[0])}~${formatAny(
                    rangeType,
                    data.range[1]
                  )}`}
                  values={[
                    {
                      fill: DEFAULT_COLOR,
                      value: `${formatCount(data.value)}${
                        unit ? ` ${unit}` : ''
                      }`,
                    },
                  ]}
                />
              );
            }
          }}
          wrapperStyle={{ zIndex: 1 }}
        />
        <Bar
          dataKey="value"
          radius={2}
          shape={(props: BarShapeProps): ReactElement => {
            const item: HistogramItem | undefined = props.payload;
            return (
              <Rectangle
                {...props}
                fill={item?.color ?? DEFAULT_COLOR}
                style={{
                  cursor: onBarClick ? 'pointer' : 'default',
                  pointerEvents: onBarClick ? 'auto' : 'none',
                }}
              />
            );
          }}
          cursor={onBarClick ? 'pointer' : 'default'}
          onClick={({ payload }: BarRectangleItem): void => {
            if (onBarClick && payload) {
              onBarClick(payload.range);
            }
          }}
        />
      </BarChart>
      {!hiddenFilter && (
        <HistogramFilter
          type={rangeType}
          range={filterRange ?? null}
          min={minRange}
          max={maxRange}
          step={step}
          plotAreaPosition={plotAreaPosition}
          onPointerMove={(event: PointerEvent<HTMLDivElement>): void => {
            // 背後にあるチャートにもホバーのイベントを伝え、Tooltipを表示させる
            if (barChartElement) {
              barChartElement.dispatchEvent(
                new window.MouseEvent('mousemove', event.nativeEvent)
              );
            }
          }}
          onDetermine={onFilterRangeChange}
        />
      )}
    </div>
  );
};

export type { HistogramItem };
export { HistogramChart };
